import React, { Fragment, useMemo, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'

import { CircleSpinner } from './loading';
import classNames from '../lib/class-names';
import { useFiles } from "../hooks/usePartyRequest";

//TODO seperate dialog content from dialog so state is cleared on re-open, leaving for now
function FilesDialog ({ 
  request, 
  isOpen,
  onFileRemoved, 
  onFileUploaded, 
  onClose 
}) {
  const form         = request.form;
  const user         = request.user;
  const agency       = request.agency;
  const partyRequest = request.party_request;
  
  const [ error, setError ]             = useState(null);
  const [ message, setMessage ]         = useState('');
  const [ isLoading, setLoading ]       = useState(false);
  const [ description, setDescription ] = useState('');
  const [ filesLoading, setFilesLoading ] = useState({});

  const inputRef = useRef(null);
  const filesContainerRef = useRef(null);
  const requirementsRef = useRef(null);

  const [ uploadFile, removeFile ] = useFiles(form.id, partyRequest.id);

  const partyUploadedFiles = useMemo(
    function filterPartyUploads () {
      var formFiles = request.form_files || []; 
      return formFiles.filter((file) => file.party_request_upload && file.party_request_id === request.party_request.id)
    },
    [ request.form_files, request.party_request.id ]
   );

  function validateUpload (files) {
    var errorMessage = null;
    if (!files || !files.length) {
      errorMessage = 'Please select a file to upload.'
    } else if (!description) {
      errorMessage = 'Please name which supporting document you have selected to upload.';
    }

    setError(errorMessage);
    return !errorMessage;
  }

  async function onRemoveFile (file) {
    if (isLoading) return;

    setError("");
    setMessage("");
    setFilesLoading(s => ({ ...s, [file.id]: true }));

    try {
      await removeFile(file.id);
      setMessage("The file was successfully removed.");
      onFileRemoved(file.id);
    } catch (err) {
      var errorMessage = 'An unknown error occurred removing the file.';

      if (err.isFriendly) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setFilesLoading(s => ({ ...s, [file.id]: false }));
    }
  }

  async function onUploadClick (e) {
    if (isLoading) return;

    setMessage('');
    
    var files = Array.from(inputRef.current?.files);
    var isValid = validateUpload(files);

    if (!isValid) return;

    var uploadedFile = null;
    setLoading(true);
    
    try {
      const [ fileToUpload ] = await readFiles(files); //NOTE* Only uploading single file from results for the moment

      if (fileToUpload) {
        uploadedFile = await uploadFile(fileToUpload, description);
      } else {
        throw mapFriendlyError('An error occurred reading the file data. Please check the file is valid and try again.');
      }
    } catch (err) {
      var errorMessage = 'An unknown error occurred uploading the file. Please try again.';

      if (err.isFriendly) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      if (uploadedFile) {
        if (inputRef.current) {
          inputRef.current.value = null;
        }

        setDescription('');
        setMessage("The file was uploaded successfully.");

        onFileUploaded({
          id:       uploadedFile.id, 
          fields:   uploadedFile.fields,
          filename: uploadedFile.filename, 
          pages:    uploadedFile.pages,
          page_dimensions: uploadedFile.page_dimensions,
          
          party_request_id:     uploadedFile.party_request_id,
          party_description:    uploadedFile.party_description,
          party_request_upload: uploadedFile.party_request_upload 
        });

        var filesEl = filesContainerRef.current;
        if (filesEl) {
          filesEl.scroll({ top: filesEl.scrollHeight, behavior: 'smooth' });
        }
      }

      setLoading(false);
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        initialFocus={requirementsRef}
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={() => null}
      >
        <div className="min-h-screen px-4 text-center">
          <Dialog.Overlay className="fixed inset-0 bg-gray-800 opacity-50" />         
             
            <span
              className="hidden md:inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="md:inline-block md:w-screen md:max-w-2xl p-4 md:p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <Dialog.Title
                as="h3"
                className="text-lg font-medium leading-6 text-gray-900"
              >
                Upload Documents
              </Dialog.Title>
              
              <div className="mb-2 mt-2">
                {!!partyRequest.documents_requested && <p className="mb-4 text-gray-800 text-base leading-5">
                  <span className="font-semibold">{user.given_name} </span>from{" "}
                  <span className="font-semibold">{agency.name}</span> has requested the below documents.
                </p>}

                {
                  !!partyRequest.documents_required && 
                  <div className="border border-gray-200 rounded-md p-4 mt-2">
                    <div className="flex items-start md:items-center space-x-3">
                      <div style={{ minWidth: '2.25rem', minHeight: '2.25rem', backgroundColor: "#ffd230"}} className="w-6 h-6 flex items-center justify-center rounded text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"  className="w-6 h-6">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                        </svg>
                      </div>
                      <span className="text-sm leading-tight md:text-base font-medium text-gray-900 m-0 mb-1 md:leading-normal"><span className="font-normal">The requested documents are required to complete a</span> Termination Notice of Residential Tenancy Agreement</span>
                    </div>
                  </div>
                }


                <details ref={requirementsRef} open className="border border-gray-200 rounded-md mt-2">
                  <summary className="cursor-pointer text-sm font-semibold text-gray-800 bg-gray-50 p-1 px-4 border-b border-gray-200">Documents Requested</summary>
                  <p className="text-sm font-normal text-gray-700 leading-tight whitespace-pre-line p-4">{partyRequest.documents_description || "Please provide any relevant documents for the Residential Termination Notice."}</p>
                </details>
              </div>

              {
                !!partyUploadedFiles.length &&
                <details 
                  open 
                  ref       = {filesContainerRef} 
                  className = "border border-gray-200 rounded-md mb-2 overflow-auto" 
                  style     = {{ maxHeight: "15rem" }}
                >
                  <summary className="cursor-pointer text-sm font-semibold text-gray-800 bg-gray-50 p-1 px-4 border-b border-gray-200">Uploaded Documents ({partyUploadedFiles.length})</summary>
                  <div className="space-y-1 p-2">
                    {partyUploadedFiles.map((file) => (
                      <FileListItem
                        key       = {file.id}
                        file      = {file}
                        isLoading = {!!filesLoading[file.id]}
                        onRemove  = {() => onRemoveFile(file)}
                      />
                   ))}
                  </div>
                </details>
              }

              <div className="p-4 bg-gray-50 rounded bg-white border border-gray-200 space-y-2">
                <div className="">
                  <div className="font-semibold text-base text-gray-800">File Upload</div>
                  <div className="text-sm text-gray-500 leading-tight">Select a supporting document to upload.</div>
                </div>

                <div className="flex-col flex md:flex-row items-center justify-start file-upload-contain space-y-1 md:space-y-0">
                  <input 
                    type      = "file" 
                    name      = "file" 
                    accept    = "application/pdf"
                    ref       = {inputRef} 
                    disabled  = {isLoading} 
                    className = {classNames(
                      "w-full flex-1 px-1 py-1 bg-white text-gray-500 text-sm rounded border placeholder-gray-400 border-gray-300"
                    )} 
                  />
                </div>

                <div>
                  <input 
                    disabled    = {isLoading} 
                    value       = {description} 
                    onChange    = {e => setDescription(e.target.value)} 
                    placeholder = "Please name which supporting document you have selected to upload" 
                    name        = "description" 
                    className   = {classNames(
                      "font-normal text-sm mt-1 appearance-none block w-full px-3 py-2 border rounded placeholder-gray-400",
                      "focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out",
                      "border-gray-300"
                    )}
                  >    
                  </input>
                </div>

                <button 
                  disabled  = {isLoading} 
                  onClick   = {onUploadClick} 
                  type      = "button" 
                  className = "space-x-2 button upload w-full flex items-center space-x-1 justify-center rounded-md border border-transparent px-4 py-2 bg-gray-800 text-base leading-6 font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:border-gray-900 focus:shadow-outline-gray transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                >
                  {isLoading && <CircleSpinner />}
                  <span>{isLoading ? 'Uploading' : 'Upload'}</span>
                </button>

              </div>

              <ErrorMessage message={error} />
              <SuccessMessage message={message} />

              <div className="flex justify-end mt-4">
                 <button
                  type="button"
                  className="border border-gray-200 mr-2 inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-transparent rounded-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                  onClick={() => !isLoading && onClose()}
                >
                  Close
                </button>
              </div>
            </div>
        </div>
      </Dialog>
    </Transition>
  )
};

function FileListItem ({ file, isLoading, onRemove }) {
  return (
    <div key={file.id} className="flex items-center space-x-1 border border-gray-200 rounded p-1">
     <div style={{minWidth: '1.5rem', minHeight: '1.5rem', backgroundColor: "#fb2c36"}} className="w-4 h-4 flex items-center justify-center rounded text-white">
       {isLoading ? <CircleSpinner /> : <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4 text-white">
        <path strokeLinecap="round" strokeLinejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
      </svg>
      }
    </div>
    <div className="flex-1 text-sm font-medium items-center flex justify-between px-2">
      <div className="flex space-x-2 items-center flex-1">
        <div>{file.party_description || file.filename}</div>
      </div>
      
      {isLoading ? <CircleSpinner className="text-gray-500 w-5 h-5" /> : 
      <button disabled={isLoading} className="focus:outline-none focus:text-gray-800 text-gray-600" onClick={onRemove}>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
        </svg>
      </button>}
    </div>
  </div> 
  )
}

function ErrorMessage ({ message }) {
  if (!message) return null;

  return (
    <div className="my-2 flex items-center rounded-md bg-red-50 p-2 space-x-2">
      <div className="shrink-0">
        <svg className="inline w-5 h-5 text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
      </div>
      <h3 className="text-sm font-medium text-red-800">{message}</h3>
    </div>
  );
}

function SuccessMessage ({ message }) {
  if (!message) return null;

  return (
    <div className="my-2 flex items-center rounded-md bg-green-50 p-2 space-x-2">
      <div className="shrink-0">
        <svg className="inline w-5 h-5 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
      </div>
      <h3 className="text-sm font-medium text-green-800">{message}</h3>
    </div>
  );
}

function readFile (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const filename = file.name;

    function onLoadEnd (e) {
      if (reader.error) reject();
      resolve({ filename, data: reader.result });
    }

    reader.addEventListener('loadend', onLoadEnd);
    reader.readAsDataURL(file);
  });
}

async function readFiles (files) {
  const readFileResults = await Promise.allSettled(files.map(file => readFile(file)));

  const filesToUpload = readFileResults
    .reduce((memo, readResult) => {
      if (readResult.status === 'fulfilled') {
        memo.push({ 
          filename: readResult.value.filename, 
          data: readResult.value.data
        });
      }

      return memo;
    }, []);

  return filesToUpload
}

function mapFriendlyError (message) {
  var err = new Error(message);
  err.isFriendly = true;
  return err;
}

export default FilesDialog;